/**
 * AddLocationModal Component
 *
 * Modal for adding new practice locations with autocomplete search
 * Allows linking existing locations or creating new ones
 */

import React, { useState, useEffect, useRef } from 'react';
import { locationService, customerLocationService } from '../services/locationService';
import { X, Search, MapPin, Building2 } from 'lucide-react';

const AddLocationModal = ({
  customerId,
  isOpen,
  onClose,
  onLocationAdded
}) => {
  const [step, setStep] = useState('search'); // 'search' or 'create'
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(null);

  // Form state for new location
  const [formData, setFormData] = useState({
    location_name: '',
    address: '',
    city: '',
    state: '',
    zip: '',  // Database column is 'zip', not 'zip_code'
    location_type: 'office',
    phone: ''
  });

  // Availability state
  const [availability, setAvailability] = useState({
    works_monday: false,
    works_tuesday: false,
    works_wednesday: false,
    works_thursday: false,
    works_friday: false,
    works_saturday: false,
    works_sunday: false,
    is_primary_location: false
  });

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const searchTimeoutRef = useRef(null);

  // Auto-search when query changes
  useEffect(() => {
    if (searchQuery.length >= 2) {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }

      searchTimeoutRef.current = setTimeout(async () => {
        try {
          setSearching(true);
          const results = await locationService.searchLocations(searchQuery);
          setSearchResults(results);
        } catch (err) {
          console.error('Search error:', err);
          setSearchResults([]);
        } finally {
          setSearching(false);
        }
      }, 300); // Debounce 300ms
    } else {
      setSearchResults([]);
    }

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchQuery]);

  const handleLinkExistingLocation = async (location) => {
    try {
      setSaving(true);
      setError(null);

      const linkData = {
        customer_id: customerId,
        location_id: location.id,
        ...availability
      };

      await customerLocationService.linkCustomerToLocation(linkData);

      // Fetch the complete linked location data
      const locations = await customerLocationService.getCustomerLocations(customerId);
      const newLocation = locations.find(loc => loc.location_id === location.id);

      onLocationAdded(newLocation);
      handleClose();
    } catch (err) {
      console.error('Error linking location:', err);
      setError('Failed to link location. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleCreateNewLocation = async (e) => {
    e.preventDefault();

    try {
      setSaving(true);
      setError(null);

      // Validate required fields
      if (!formData.location_name.trim()) {
        setError('Location name is required');
        return;
      }

      // Create new location
      const newLocation = await locationService.createLocation(formData);

      // Link to customer
      const linkData = {
        customer_id: customerId,
        location_id: newLocation.id,
        ...availability
      };

      await customerLocationService.linkCustomerToLocation(linkData);

      // Fetch the complete linked location data
      const locations = await customerLocationService.getCustomerLocations(customerId);
      const linkedLocation = locations.find(loc => loc.location_id === newLocation.id);

      onLocationAdded(linkedLocation);
      handleClose();
    } catch (err) {
      console.error('Error creating location:', err);
      setError('Failed to create location. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    setStep('search');
    setSearchQuery('');
    setSearchResults([]);
    setSelectedLocation(null);
    setFormData({
      location_name: '',
      address: '',
      city: '',
      state: '',
      zip: '',  // Database column is 'zip'
      location_type: 'office',
      phone: ''
    });
    setAvailability({
      works_monday: false,
      works_tuesday: false,
      works_wednesday: false,
      works_thursday: false,
      works_friday: false,
      works_saturday: false,
      works_sunday: false,
      is_primary_location: false
    });
    setError(null);
    onClose();
  };

  const toggleDay = (day) => {
    setAvailability(prev => ({ ...prev, [day]: !prev[day] }));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={handleClose}
      />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
            <h2 className="text-xl font-bold text-gray-900">
              Add Practice Location
            </h2>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Error message */}
          {error && (
            <div className="mx-6 mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}

          {/* Content */}
          <div className="p-6">
            {step === 'search' && (
              <div className="space-y-4">
                {/* Search section */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Search Existing Locations
                  </label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="Search by name, address, or city..."
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>

                {/* Search results */}
                {searching && (
                  <div className="text-center py-4">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                  </div>
                )}

                {!searching && searchResults.length > 0 && (
                  <div className="space-y-2">
                    <p className="text-sm text-gray-600">
                      Found {searchResults.length} location{searchResults.length !== 1 ? 's' : ''}
                    </p>
                    <div className="space-y-2 max-h-60 overflow-y-auto">
                      {searchResults.map((location) => (
                        <div
                          key={location.id}
                          onClick={() => setSelectedLocation(location)}
                          className={`
                            p-3 border-2 rounded-lg cursor-pointer transition-all
                            ${selectedLocation?.id === location.id
                              ? 'border-blue-600 bg-blue-50'
                              : 'border-gray-200 hover:border-blue-300'
                            }
                          `}
                        >
                          <h4 className="font-semibold text-gray-900">{location.location_name}</h4>
                          {location.address && (
                            <p className="text-sm text-gray-600 mt-1">
                              <MapPin className="w-3 h-3 inline mr-1" />
                              {location.address}, {location.city}, {location.state}
                            </p>
                          )}
                          {location.location_type && (
                            <p className="text-xs text-gray-500 mt-1">
                              <Building2 className="w-3 h-3 inline mr-1" />
                              {location.location_type.charAt(0).toUpperCase() + location.location_type.slice(1)}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {!searching && searchQuery.length >= 2 && searchResults.length === 0 && (
                  <div className="text-center py-6 text-gray-500">
                    No locations found. Create a new one below.
                  </div>
                )}

                {/* Availability selection (for linking existing location) */}
                {selectedLocation && (
                  <div className="space-y-3 pt-4 border-t border-gray-200">
                    <label className="block text-sm font-medium text-gray-700">
                      When are you available at this location?
                    </label>
                    <div className="grid grid-cols-4 gap-2">
                      {[
                        { key: 'works_monday', label: 'Mon' },
                        { key: 'works_tuesday', label: 'Tue' },
                        { key: 'works_wednesday', label: 'Wed' },
                        { key: 'works_thursday', label: 'Thu' },
                        { key: 'works_friday', label: 'Fri' },
                        { key: 'works_saturday', label: 'Sat' },
                        { key: 'works_sunday', label: 'Sun' }
                      ].map(({ key, label }) => (
                        <button
                          key={key}
                          type="button"
                          onClick={() => toggleDay(key)}
                          className={`
                            px-3 py-2 text-sm font-medium rounded-lg transition-colors
                            ${availability[key]
                              ? 'bg-green-600 text-white'
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                            }
                          `}
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                    <label className="flex items-center mt-3">
                      <input
                        type="checkbox"
                        checked={availability.is_primary_location}
                        onChange={(e) => setAvailability(prev => ({
                          ...prev,
                          is_primary_location: e.target.checked
                        }))}
                        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      />
                      <span className="ml-2 text-sm text-gray-700">Set as primary location</span>
                    </label>
                    <button
                      onClick={() => handleLinkExistingLocation(selectedLocation)}
                      disabled={saving}
                      className="w-full mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                    >
                      {saving ? 'Adding...' : 'Add This Location'}
                    </button>
                  </div>
                )}

                {/* Divider */}
                <div className="relative py-4">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-gray-300"></div>
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="px-4 bg-white text-gray-500">Or</span>
                  </div>
                </div>

                {/* Create new button */}
                <button
                  onClick={() => setStep('create')}
                  className="w-full px-4 py-2 border-2 border-dashed border-gray-300 text-gray-700 rounded-lg hover:border-blue-400 hover:text-blue-600 transition-colors"
                >
                  Create New Practice Location
                </button>
              </div>
            )}

            {step === 'create' && (
              <form onSubmit={handleCreateNewLocation} className="space-y-4">
                {/* Location details */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Location Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.location_name}
                    onChange={(e) => setFormData(prev => ({ ...prev, location_name: e.target.value }))}
                    placeholder="e.g., Main Street Medical Center"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Address
                  </label>
                  <input
                    type="text"
                    value={formData.address}
                    onChange={(e) => setFormData(prev => ({ ...prev, address: e.target.value }))}
                    placeholder="123 Main Street"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      City
                    </label>
                    <input
                      type="text"
                      value={formData.city}
                      onChange={(e) => setFormData(prev => ({ ...prev, city: e.target.value }))}
                      placeholder="City"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      State
                    </label>
                    <input
                      type="text"
                      value={formData.state}
                      onChange={(e) => setFormData(prev => ({ ...prev, state: e.target.value }))}
                      placeholder="State"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      ZIP Code
                    </label>
                    <input
                      type="text"
                      value={formData.zip}
                      onChange={(e) => setFormData(prev => ({ ...prev, zip: e.target.value }))}
                      placeholder="12345"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Phone
                    </label>
                    <input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                      placeholder="(555) 123-4567"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Location Type
                  </label>
                  <select
                    value={formData.location_type}
                    onChange={(e) => setFormData(prev => ({ ...prev, location_type: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="office">Office</option>
                    <option value="hospital">Hospital</option>
                    <option value="clinic">Clinic</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                {/* Availability */}
                <div className="pt-4 border-t border-gray-200">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    When are you available at this location?
                  </label>
                  <div className="grid grid-cols-4 gap-2">
                    {[
                      { key: 'works_monday', label: 'Mon' },
                      { key: 'works_tuesday', label: 'Tue' },
                      { key: 'works_wednesday', label: 'Wed' },
                      { key: 'works_thursday', label: 'Thu' },
                      { key: 'works_friday', label: 'Fri' },
                      { key: 'works_saturday', label: 'Sat' },
                      { key: 'works_sunday', label: 'Sun' }
                    ].map(({ key, label }) => (
                      <button
                        key={key}
                        type="button"
                        onClick={() => toggleDay(key)}
                        className={`
                          px-3 py-2 text-sm font-medium rounded-lg transition-colors
                          ${availability[key]
                            ? 'bg-green-600 text-white'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          }
                        `}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                  <label className="flex items-center mt-3">
                    <input
                      type="checkbox"
                      checked={availability.is_primary_location}
                      onChange={(e) => setAvailability(prev => ({
                        ...prev,
                        is_primary_location: e.target.checked
                      }))}
                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <span className="ml-2 text-sm text-gray-700">Set as primary location</span>
                  </label>
                </div>

                {/* Actions */}
                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setStep('search')}
                    className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Back
                  </button>
                  <button
                    type="submit"
                    disabled={saving}
                    className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                  >
                    {saving ? 'Creating...' : 'Create Location'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddLocationModal;
